<template>
  <v-container fluid class="pa-6">
    <div class="d-flex justify-space-between align-center mb-6">
      <h1 class="text-h5 font-weight-bold text-grey-darken-4">Lista de Imóveis</h1>
      <v-btn color="primary" @click="handleCreateProperty">
        <v-icon start>mdi-plus</v-icon>
        Adicionar Novo Imóvel
      </v-btn>
    </div>

    <v-card class="elevation-4">
      <v-card-text>
        <!-- Filtros -->
        <v-row class="mb-6" dense>
          <v-col cols="12" md="8">
            <v-text-field v-model="searchTerm" placeholder="Pesquisar por título, cidade ou bairro" clearable
              hide-details prepend-inner-icon="mdi-magnify" @input="onFiltersChange" />
          </v-col>
          <v-col cols="12" md="4">
            <v-select v-model="selectedStatus" :items="statusOptions" placeholder="Filtrar por Status"
               clearable hide-details @update:model-value="onFiltersChange" />
          </v-col>
        </v-row>

        <!-- Tabela -->
        <v-data-table-server :headers="columns" :items="paginatedProperties" :items-length="totalItems"
          :loading="isLoading" :items-per-page="itemsPerPage" :page="page" :no-data-text="noDataMessage"
          class="elevation-0" @update:page="page = $event" @update:items-per-page="itemsPerPage = $event">
          <template v-slot:item.id="{ item }">
            <span>{{ formatCode(item) }}</span>
          </template>

          <template v-slot:item.type="{ item }">
            <span>{{ formatType(item.type) }}</span>
          </template>

          <template v-slot:item.operation="{ item }">
            <span>{{ formatOperation(item.operation) }}</span>
          </template>

          <template v-slot:item.price="{ item }">
            <span>R$ {{ item.price.toLocaleString('pt-BR') }}</span>
          </template>

          <template v-slot:item.status="{ item }">
            <v-chip :color="statusTagMap[item.status]" label>
              {{ formatStatus(item.status) }}
            </v-chip>
          </template>

          <template v-slot:item.is_reserved="{ item }">
            <v-chip :color="isActiveTagMap[item.is_reserved]" label>
              {{ formatIsReserved(item.is_reserved) }}
            </v-chip>
          </template>

          <template v-slot:item.is_active="{ item }">
            <v-chip :color="isActiveTagMap[item.is_active]" label>
              {{ formatIsActive(item.is_active) }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-icon icon="mdi-check-circle-outline" size="large" color="info" class="mr-2"
              @click="handleStatusChange(item)" title="Alterar Status"/>
            <v-icon icon="mdi-pencil" color="warning" size="large" class="mr-2" @click="handleEdit(item)"
              title="Editar" />
            <!--<v-icon icon="mdi-delete" color="error" @click="handleDelete(item)" title="Excluir" />-->
          </template>
        </v-data-table-server>

        <v-alert v-if="hasError && !isLoading" type="error" variant="tonal" class="mt-4 text-center">
          Ocorreu um erro ao carregar os dados.
        </v-alert>
      </v-card-text>
    </v-card>

  </v-container>

  <!-- Dialog exclusão -->
  <v-dialog v-model="deleteDialog" max-width="500">
    <v-card>
      <v-card-title class="text-h5">Confirmar Exclusão</v-card-title>
      <v-card-text>
        Tem certeza de que deseja excluir o imóvel
        <strong>{{ itemToDelete.title }}</strong>?
        <br /><br />
        Código: <strong>{{ formatCode(itemToDelete) }}</strong>
      </v-card-text>
      <v-card-actions class="d-flex justify-space-between">
        <v-btn color="secondary" variant="flat" @click="deleteDialog = false">
          Cancelar
        </v-btn>
        <v-btn color="error" variant="flat" @click="confirmDelete">
          Excluir
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="statusDialog" max-width="500">
    <v-card>
      <v-card-title class="text-h5">Alterar Status do Imóvel</v-card-title>
      <v-card-text>
        <p class="mb-4">
          Imóvel: <strong>{{ propertyToUpdate.title }}</strong> (Cód:
          <strong>{{ formatCode(propertyToUpdate) }}</strong>)
        </p>

        <v-select v-model="newStatusData.status" :items="statusOptions" item-title="title" item-value="value"
          label="Novo Status" :rules="[v => !!v || 'O status é obrigatório']" variant="outlined" density="compact"
          class="mt-4"></v-select>

        <v-switch v-model="newStatusData.notifyClient" color="primary" hide-details class="mt-4">
          <template #label>
            Notificar Cliente sobre alteração?
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="small" class="ml-1" color="grey-darken-1">mdi-information-outline</v-icon>
              </template>
              <span>
                Se 'Sim', o cliente será notificado (via email/sistema) sobre a
                aprovação ou rejeição.
              </span>
            </v-tooltip>
          </template>
        </v-switch>
      </v-card-text>
      <v-card-actions class="d-flex justify-space-between">
        <v-btn color="secondary" variant="flat" @click="statusDialog = false">
          Cancelar
        </v-btn>
        <v-btn color="primary" variant="flat" :loading="isStatusSubmitting" :disabled="isStatusSubmitting" @click="confirmStatusChange">
          Salvar Status
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/services/api'
import { useToastStore } from '@/store/toast'
import { useLoadingOverlay } from '@/store/loadingOverlay'

const router = useRouter()
const toastStore = useToastStore()
const loadingOverlay = useLoadingOverlay()

// Estado
const userData = ref(null)
const properties = ref([])
const isLoading = ref(true)
const hasError = ref(false)
const searchTerm = ref('')
const selectedStatus = ref(null)

// Paginação
const page = ref(1)
const itemsPerPage = ref(10)
const totalItems = ref(0)

// Exclusão
const deleteDialog = ref(false)
const itemToDelete = ref({})

const statusDialog = ref(false)
const isStatusSubmitting = ref(false)
const propertyToUpdate = ref({})
const newStatusData = ref({
  status: null,
  notifyClient: true
})

// Filtros
const statusOptions = [
  { title: 'Em Análise', value: 'in_analysis' },
  { title: 'Aprovado', value: 'approved' },
  { title: 'Rejeitado', value: 'rejected' }
]

const statusTagMap = {
  in_analysis: 'info',
  approved: 'success',
  rejected: 'error'
}

const isActiveTagMap = {
  0: 'error',
  1: 'success'
}

// Dados filtrados
const filteredProperties = computed(() => {
  let result = properties.value

  if (searchTerm.value) {
    const term = searchTerm.value.toLowerCase()
    result = result.filter(
      (property) =>
        property.title.toLowerCase().includes(term) ||
        property.city.toLowerCase().includes(term) ||
        property.neighborhood.toLowerCase().includes(term)
    )
  }

  if (selectedStatus.value) {
    result = result.filter(
      (property) => property.status === selectedStatus.value
    )
  }

  totalItems.value = result.length
  return result
})

// Paginação (slice local)
const paginatedProperties = computed(() => {
  const start = (page.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return filteredProperties.value.slice(start, end)
})

// Mensagem
const noDataMessage = computed(() => {
  if (isLoading.value) return 'Carregando...'
  if (filteredProperties.value.length === 0) return 'Nenhum imóvel encontrado.'
  return ''
})

// Colunas
const columns = [
  { title: 'Código', key: 'id', sortable: false },
  { title: 'Título', key: 'title', sortable: true },
  { title: 'Tipo', key: 'type', sortable: true },
  { title: 'Operação', key: 'operation', sortable: true },
  { title: 'Preço', key: 'price', sortable: true },
  { title: 'Status', key: 'status', sortable: true },
  { title: 'Reservado', key: 'is_reserved', sortable: true },
  { title: 'Situação', key: 'is_active', sortable: true },
  { title: 'Ações', key: 'actions', sortable: false }
]

// Funções API
const loadProperties = async () => {
  isLoading.value = true
  hasError.value = false
  loadingOverlay.startLoading()
  try {
    let response = null
    if (userData.value.user.role === 'broker') {
      response = await api.get('/properties/fetch', {
        params: { broker_id: userData.value.user.id }
      })
    } else {
      response = await api.get('/properties/fetch')
    }

    const enterpriseData = response.data.data[0]

    if (enterpriseData && enterpriseData.properties) {
      properties.value = enterpriseData.properties
    } else {
      properties.value = []
      console.warn('The API response did not contain the expected property list.')
    }
  } catch (error) {
    console.error('Erro ao carregar imóveis:', error)
    hasError.value = true
    toastStore.showToast({
      message:
        error?.response?.data?.error?.message ||
        'Ocorreu um erro ao carregar a lista de imóveis.',
      color: 'error'
    })
  } finally {
    isLoading.value = false
    loadingOverlay.stopLoading()
  }
}

// CRUD
const handleCreateProperty = () => {
  router.push('/app/imoveis/novo')
}

const handleEdit = async (row) => {
  router.push({ name: 'property-edit', params: { id: row.id } })
}

const handleDelete = (item) => {
  itemToDelete.value = item
  deleteDialog.value = true
}

const confirmDelete = async () => {
  deleteDialog.value = false
  try {
    await api.delete(`/properties/${itemToDelete.value.id}`)
    toastStore.showToast({
      message: 'Imóvel excluído com sucesso!',
      color: 'success'
    })
    await loadProperties()
  } catch (error) {
    console.error('Erro ao excluir imóvel:', error)
    toastStore.showToast({
      message:
        error?.response?.data?.error?.message || 'Erro ao excluir imóvel. Tente novamente.',
      color: 'error'
    })
  }
}

const handleStatusChange = (item) => {
  propertyToUpdate.value = item
  newStatusData.value.status = item.status
  statusDialog.value = true
}

const confirmStatusChange = async () => {
  isStatusSubmitting.value = true
  
  try {
    const payload = {
      status: newStatusData.value.status,
      notify_client: newStatusData.value.notifyClient 
    }

    await api.put(`/properties/${propertyToUpdate.value.id}/status`, payload)
    
    toastStore.showToast({
      message: 'Status do imóvel atualizado com sucesso!',
      color: 'success'
    })

    await loadProperties()

    statusDialog.value = false
  } catch (error) {
    console.error('Erro ao alterar status:', error)
    toastStore.showToast({
      message:
        error?.response?.data?.error?.message || 'Erro ao alterar o status do imóvel. Tente novamente.',
      color: 'error'
    })
  } finally {
    isStatusSubmitting.value = false
  }
}

// Utilitários
function formatOperation(operation) {
  const map = {
    rent: 'Locação',
    sale: 'Venda',
    launch: 'Lançamento',
    both: 'Venda e Locação'
  }
  return map[operation] || operation
}

function formatType(type) {
  const map = {
    apartment: 'Apartamento',
    house: 'Casa',
    land: 'Terreno',
    rural: 'Rural',
    commercial: 'Comercial'
  }
  return map[type] || type
}

function formatStatus(status) {
  const map = {
    in_analysis: 'Em Análise',
    approved: 'Aprovado',
    rejected: 'Rejeitado'
  }
  return map[status] || status
}

function formatIsReserved(is_reserved) {
  return is_reserved === 1 ? 'Sim' : 'Não'
}

function formatIsActive(is_active) {
  return is_active === 1 ? 'Ativo' : 'Desativado'
}

function formatCode(property) {
  const prefixMap = {
    rent: 'L-',
    sale: 'V-',
    launch: 'LA-',
    both: 'VL-'
  }
  const prefix = prefixMap[property.operation] || ''
  return `${prefix}${property.id}-${property.year}`
}

// Recarregar quando filtros mudam
function onFiltersChange() {
  page.value = 1
}

// Lifecycle
onMounted(() => {
  const storedData = localStorage.getItem('userData')
  if (storedData) {
    userData.value = JSON.parse(storedData)
  }
  loadProperties()
})
</script>
